import { Hole } from "../../models/scoreModel";

export interface PlayerScore {
  playerId: string;
  score: number; // Gross score for the hole
  isConceded: boolean; // If the player conceded the hole
  playerHandicap: number; // Player's handicap
}

interface NetScore {
  playerId: string;
  netScore: number;
  isConceded: boolean;
}

export const getWLDSingleHoleScore = (
  playerScores: PlayerScore[],
  holeDetails: Hole
) => {
  const validPlayers = playerScores.filter(
    (p) => (p.score > 0 && !p.isConceded) || (p.score === 0 && p.isConceded)
  );
  const totalPlayers = validPlayers.length;
  const maxComparisonCount = totalPlayers - 1; // Maximum possible wins/losses/draws per player

  // First, check if all but one player has conceded
  const nonConcededPlayers = validPlayers.filter((p) => !p.isConceded);
  if (nonConcededPlayers.length === 1) {
    const retColl = validPlayers.map((player) => ({
      playerId: player.playerId,
      win:
        player.playerId === nonConcededPlayers[0].playerId
          ? maxComparisonCount
          : 0,
      loss: player.isConceded ? maxComparisonCount : 0,
      draw: 0,
    }));
    return retColl;
  }

  // Calculate net scores for non-conceded players
  const netScores = validPlayers
    .map((player) => {
      if (player.isConceded || player.score === 0) return null;

      const strokesGiven =
        Math.floor(player.playerHandicap / 18) +
        (holeDetails.strokeIndex <= player.playerHandicap % 18 ? 1 : 0);

      return {
        playerId: player.playerId,
        netScore: player.score - strokesGiven,
      };
    })
    .filter((score): score is NonNullable<typeof score> => score !== null);

  // Compare each player against others
  const holeScores = validPlayers.map((player) => {
    if (player.isConceded) {
      return {
        playerId: player.playerId,
        win: 0,
        loss: maxComparisonCount,
        draw: 0,
      };
    }

    const playerNet = netScores.find((s) => s.playerId === player.playerId);
    if (!playerNet) {
      return { playerId: player.playerId, win: 0, loss: 0, draw: 0 };
    }

    let wins = 0,
      losses = 0,
      draws = 0;

    // Compare with other non-conceded players
    netScores.forEach((opponent) => {
      if (opponent.playerId !== player.playerId) {
        if (playerNet.netScore < opponent.netScore) wins++;
        else if (playerNet.netScore > opponent.netScore) losses++;
        else draws++;
      }
    });

    const concededCount = validPlayers.filter((p) => p.isConceded).length;
    wins = Math.min(wins + concededCount, maxComparisonCount);

    // Ensure total comparisons don't exceed maxComparisonCount
    const total = wins + losses + draws;
    if (total > maxComparisonCount) {
      const excess = total - maxComparisonCount;
      draws = Math.max(0, draws - excess);
    }

    return { playerId: player.playerId, win: wins, loss: losses, draw: draws };
  });

  return holeScores;
};

function generateAllPlayerMatchups(
  players: NetScore[]
): [NetScore, NetScore][] {
  const matches: [NetScore, NetScore][] = [];
  const n = players.length;

  if (n < 2) {
    throw new Error("At least 2 players are required to generate matches.");
  }

  // Generate all unique player pairings
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      matches.push([players[i], players[j]]);
    }
  }

  return matches;
}

export const getScoresBetweenPlayers = (
  playerScores: PlayerScore[],
  holeDetails: Hole,
  byPlayerId: string,
) => {
  const validPlayers = playerScores.filter(
    (p) => (p.score > 0 && !p.isConceded) || (p.score === 0 && p.isConceded)
  );
  // Calculate net scores for each player
  const netScores = validPlayers.map((player) => {
    const strokesGiven =
      Math.floor(player.playerHandicap / 18) +
      (holeDetails.strokeIndex <= player.playerHandicap % 18 ? 1 : 0);

    return {
      playerId: player.playerId,
      netScore: player.score - strokesGiven,
      isConceded: player.isConceded,
    };
  });

  // Create pairs of players
  const results: Array<{
    playerId: string;
    opponentId: string;
    win: number;
    loss: number;
    draw: number;
  }> = [];

  const uniquePairs = generateAllPlayerMatchups(netScores);

  uniquePairs.forEach(([player1, player2]) => {
    if (player2.playerId === byPlayerId) {
      const x = {...player1};
      player1 = {...player2};
      player2 = x;

    }
    if (player1.isConceded && player2.isConceded) {
      results.push({
        playerId: player1.playerId,
        opponentId: player2.playerId,
        win: 0,
        loss: 0,
        draw: 1,
      });
    } else if (player1.isConceded) {
      results.push({
        playerId: player1.playerId,
        opponentId: player2.playerId,
        win: 0,
        loss: 1,
        draw: 0,
      });
    } else if (player2.isConceded) {
      results.push({
        playerId: player1.playerId,
        opponentId: player2.playerId,
        win: 1,
        loss: 0,
        draw: 0,
      });
    } else {
      if (player1.netScore < player2.netScore) {
        results.push({
          playerId: player1.playerId,
          opponentId: player2.playerId,
          win: 1,
          loss: 0,
          draw: 0,
        });
      } else if (player1.netScore > player2.netScore) {
        results.push({
          playerId: player1.playerId,
          opponentId: player2.playerId,
          win: 0,
          loss: 1,
          draw: 0,
        });
      } else {
        results.push({
          playerId: player1.playerId,
          opponentId: player2.playerId,
          win: 0,
          loss: 0,
          draw: 1,
        });
      }
    }
  });

  return results;
};

// this is for Data entry role. where we get all possible combinations of player scores.

const getPlayerMatchCombinations = (players: NetScore[]) => {
  const playerMatchups: Array<[NetScore, NetScore]> = [];
  for (let i = 0; i < players.length; i++) {
    for (let j = 0; j < players.length; j++) {
      if (i !== j) {
        playerMatchups.push([players[i], players[j]]);
      }
    }
  }
  return playerMatchups;
};

export const getAllCombinationScoresBetweenPlayers = (
  playerScores: PlayerScore[],
  holeDetails: Hole
) => {
  const validPlayers = playerScores.filter(
    (p) => (p.score > 0 && !p.isConceded) || (p.score === 0 && p.isConceded)
  );
  // Calculate net scores for each player
  const netScores = validPlayers.map((player) => {
    const strokesGiven =
      Math.floor(player.playerHandicap / 18) +
      (holeDetails.strokeIndex <= player.playerHandicap % 18 ? 1 : 0);

    return {
      playerId: player.playerId,
      netScore: player.score - strokesGiven,
      isConceded: player.isConceded,
    };
  });

  // Create pairs of players
  const results: Array<{
    playerId: string;
    opponentId: string;
    win: number;
    loss: number;
    draw: number;
  }> = [];

  const uniquePairs = getPlayerMatchCombinations(netScores);

  uniquePairs.forEach(([player1, player2]) => {
    if (player1.isConceded && player2.isConceded) {
      results.push({
        playerId: player1.playerId,
        opponentId: player2.playerId,
        win: 0,
        loss: 0,
        draw: 1,
      });
    } else if (player1.isConceded) {
      results.push({
        playerId: player1.playerId,
        opponentId: player2.playerId,
        win: 0,
        loss: 1,
        draw: 0,
      });
    } else if (player2.isConceded) {
      results.push({
        playerId: player1.playerId,
        opponentId: player2.playerId,
        win: 1,
        loss: 0,
        draw: 0,
      });
    } else {
      if (player1.netScore < player2.netScore) {
        results.push({
          playerId: player1.playerId,
          opponentId: player2.playerId,
          win: 1,
          loss: 0,
          draw: 0,
        });
      } else if (player1.netScore > player2.netScore) {
        results.push({
          playerId: player1.playerId,
          opponentId: player2.playerId,
          win: 0,
          loss: 1,
          draw: 0,
        });
      } else {
        results.push({
          playerId: player1.playerId,
          opponentId: player2.playerId,
          win: 0,
          loss: 0,
          draw: 1,
        });
      }
    }
  });

  return results;
};
